import styled, { keyframes } from "styled-components";
import { Link } from "wouter";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  ANIMATION,
  GRADIENTS,
} from "../../utils/constants";

const PLAN_SKELETON_CARD_HEIGHT = 144;

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

export const SkeletonBox = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  background: linear-gradient(
    90deg,
    var(--color-surface) 0%,
    var(--color-hover-overlay) 50%,
    var(--color-surface) 100%
  );
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite linear;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  opacity: 0.6;

  /* Performance optimizations for 60fps */
  transform: translateZ(0);
  will-change: background-position;
  contain: layout style paint;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${GRADIENTS.skeleton5};
    animation: ${pulse} 1.5s ease-in-out infinite;

    /* GPU acceleration for pulse animation */
    will-change: opacity;
  }
`;

export const ChartSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md + SPACING.xs}px;
  width: 100%;
  height: 100%;
  padding: ${SPACING.sm}px 0;
`;

export const ChartSkeletonTitle = styled(SkeletonBox)`
  height: 32px;
  width: 240px;
  margin-bottom: ${SPACING.sm}px;
  border-radius: 6px;
`;

export const ChartSkeletonBody = styled(SkeletonBox)<{ height?: number }>`
  height: ${(props) => props.height || 300}px;
  width: 100%;
  border-radius: 12px;
  background: var(--color-surface);
  background: linear-gradient(
    90deg,
    var(--color-surface) 0%,
    var(--color-hover-overlay) 50%,
    var(--color-surface) 100%
  );
  background-size: 1000px 100%;
  opacity: 0.5;
`;

export const HeroSkeletonBox = styled(SkeletonBox)`
  height: 160px;
  width: 100%;
  border-radius: 16px;
  opacity: 1;
`;

export const Wrapper = styled.div`
  background: var(--color-background-0);
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-height: 0%;
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};
  grid-template-areas:
    "hero"
    "timeline"
    "plan"
    "trend";
  padding: ${SPACING_RESPONSIVE.mobile.sectionGap}px
    ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  row-gap: ${SPACING_RESPONSIVE.mobile.sectionGap}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.sectionGap}px
      ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    row-gap: ${SPACING_RESPONSIVE.tablet.sectionGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas:
      "hero hero"
      "timeline timeline"
      "plan plan"
      "trend trend";
  }
`;

export const PlanSection = styled.section`
  grid-area: plan;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg}px;
  min-width: 0;
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;
  border: 1px solid var(--color-border);
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  box-shadow: var(--shadow-medium);

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
  }
`;

export const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px ${SPACING.lg}px;
`;

export const PlanHeaderTitle = styled.h3`
  margin: 0;
  font-size: var(--text-xxl);
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.4;
  color: var(--color-text-default);
`;

export const PlanLink = styled(Link)`
  font-weight: 600;
  color: var(--color-primary);
  text-decoration: none;
  text-underline-offset: ${SPACING.xs}px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: ${SPACING.xs}px;
    border-radius: ${SPACING.xxs}px;
  }
`;

export const PlanHeaderLink = styled(PlanLink)`
  display: inline-flex;
  align-items: center;
  min-height: ${SPACING.xl + SPACING.sm + SPACING.xs}px;
  font-size: var(--text-sm);
`;

export const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${SPACING.lg}px ${SPACING.md}px;

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: ${SPACING.lg}px;
  }
`;

export const PlanFooterNote = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const PlanSkeletonCard = styled(SkeletonBox)`
  height: ${PLAN_SKELETON_CARD_HEIGHT}px;
  border: 0;
  opacity: 1;
`;

export const TimelineSectionRow = styled.div`
  grid-area: timeline;
  display: flex;
  flex-direction: column;
  gap: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;

  @media ${QUERIES.tabletAndUp} {
    gap: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    flex-direction: row;
    align-items: stretch;
    gap: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
  }
`;

export const RecentTransactionsCard = styled.div`
  background: var(--color-card-background);
  border-radius: 16px;
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;
  box-shadow: var(--shadow-medium);
  border: 1px solid var(--color-border);
  transition: ${ANIMATION.transitions.allSlow};
  display: flex;
  flex-direction: column;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    flex: 0 0 400px;
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
  }
`;

export const TimelineChartWrapper = styled.div`
  flex: 1;
  min-width: 0;
  transition: ${ANIMATION.transitions.allSlow};
  background: var(--color-card-background);
  border-radius: 16px;
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;
  box-shadow: var(--shadow-large);
  border: 1px solid var(--color-border);

  h3 {
    color: var(--color-text-default);
    font-family: "Crimson Pro", sans-serif;
    margin: 0 0 ${SPACING.md}px 0;
  }

  p {
    color: var(--color-text-default);
    text-align: center;
    font-size: var(--text-md);
  }

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
    min-height: 400px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
    min-height: 400px;
  }
`;

export const PeriodControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;

  > :last-child {
    min-width: 90px;
  }
`;

// Timeline Chart Components
export const TimelineHeader = styled.div`
  margin-bottom: ${SPACING.md}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
`;

export const ChartTitle = styled.h3`
  margin: 0;
  color: var(--color-text-default);
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.4;
`;

export const ChartTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
`;

export const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
  flex-wrap: wrap;
`;

export const ChartLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs + SPACING.xxs}px;
`;

export const ChartLegendDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const ChartLegendLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 400;
  letter-spacing: 0.025em;
  color: var(--color-text-secondary);
`;

export const ChartCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${SPACING.sm + SPACING.xs}px;
  flex-wrap: wrap;
  gap: ${SPACING.sm + SPACING.xs}px;
`;

export const TrendChartFill = styled.div`
  flex: 1;
  width: 100%;
  min-height: 320px;
`;

export const EmptyStateText = styled.p`
  text-align: center;
  color: var(--color-text-default);
`;

export const HeroWrapper = styled.div`
  grid-area: hero;
  background: var(--hero-background);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  box-shadow: var(--shadow-large);
  overflow: hidden;
  transition: ${ANIMATION.transitions.allSlow};
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};
  display: flex;
  flex-direction: column;
`;

export const HeroInner = styled.div`
  display: flex;
  flex-direction: column;

  @media ${QUERIES.laptopAndUp} {
    flex-direction: row;
    min-height: 160px;
  }
`;

export const HeroLeftPanel = styled.div`
  padding: ${SPACING.md}px ${SPACING.md + SPACING.xs}px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm + SPACING.xxs}px;
  border-bottom: 1px solid var(--color-border);

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING.md + SPACING.xs}px ${SPACING.lg}px;
  }

  @media ${QUERIES.laptopAndUp} {
    border-bottom: none;
    border-right: 1px solid var(--color-border);
    flex: 0 0 35%;
    justify-content: center;
    padding: ${SPACING.lg}px ${SPACING.lg + SPACING.xs}px;
  }
`;

export const HeroTopRow = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
  }
`;

export const HeroBottomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid var(--color-border);

  @media ${QUERIES.laptopAndUp} {
    display: flex;
  }
`;

export const HeroCell = styled.div<{ $divider?: boolean }>`
  flex: 1;
  padding: ${SPACING.sm + SPACING.xs + SPACING.xxs}px
    ${SPACING.md + SPACING.xxs}px;
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  gap: ${SPACING.xs + SPACING.xxs}px;
  min-width: 0;
  border-top: 1px solid var(--color-border);

  &:first-child {
    border-top: none;
  }

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING.md}px ${SPACING.md + SPACING.xs + SPACING.xxs}px;
    border-top: none;
    border-left: ${({ $divider }) =>
      $divider ? "1px solid var(--color-border)" : "none"};
    justify-content: flex-end;
  }
`;

export const HeroBottomCell = styled.div<{ $first?: boolean }>`
  flex: 1;
  padding: ${SPACING.sm + SPACING.xs + SPACING.xxs}px
    ${SPACING.md + SPACING.xxs}px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs + SPACING.xxs}px;
  min-width: 0;
  border-left: ${({ $first }) =>
    $first ? "none" : "1px solid var(--color-border)"};

  @media ${QUERIES.laptopDown} {
    &:nth-child(odd) {
      border-left: none;
    }
    &:nth-child(even) {
      border-left: 1px solid var(--color-border);
    }
    &:nth-child(n + 3) {
      border-top: 1px solid var(--color-border);
    }
  }
  @media ${QUERIES.tabletAndUp} {
    display: grid;
    grid-template-columns: 1fr auto;
    row-gap: ${SPACING.xs + SPACING.xxs}px;
    column-gap: ${SPACING.xs}px;

    > *:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
      align-self: center;
    }
    > *:nth-child(2) {
      grid-column: 1 / -1;
      grid-row: 2;
    }
    > *:nth-child(3) {
      grid-column: 2;
      grid-row: 1;
      align-self: center;
    }
  }

  @media ${QUERIES.laptopAndUp} {
    border-left: ${({ $first }) =>
      $first ? "none" : "1px solid var(--color-border)"};
  }
`;

export const HeroBottomCellDotLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs + SPACING.xxs}px;
`;

export const HeroLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

export const HeroLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
`;

export const HeroNetWorthValue = styled.span`
  font-size: var(--text-display-xl);
  font-weight: 700;
  font-family: "Crimson Pro", sans-serif;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: var(--color-text-default);
`;

export const HeroValue = styled.span<{ $color?: string; $small?: boolean }>`
  font-size: ${({ $small }) => ($small ? "var(--text-md)" : "var(--text-xxl)")};
  font-weight: 700;
  font-family: "Crimson Pro", sans-serif;
  line-height: 1.2;
  letter-spacing: ${({ $small }) => ($small ? "0" : "-0.01em")};
  color: ${({ $color }) => $color ?? "var(--color-text-default)"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const HeroSubtitle = styled.span`
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  line-height: 1.4;
`;

export const HeroBadge = styled.span<{ $negative?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px ${SPACING.sm + SPACING.xxs}px;
  border-radius: 20px;
  font-size: var(--text-xs);
  font-weight: 600;
  width: fit-content;
  background: ${({ $negative }) =>
    $negative
      ? "var(--color-error-background)"
      : "var(--color-success-background)"};
  color: ${({ $negative }) =>
    $negative ? "var(--color-error)" : "var(--color-success)"};
`;

export const HeroDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

// Combined trend + treemap section
export const TrendAndTreemapWrapper = styled.div`
  grid-area: trend;
  display: flex;
  flex-direction: column;
  gap: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;

  @media ${QUERIES.tabletAndUp} {
    gap: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    flex-direction: row;
    align-items: stretch;
    gap: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
  }
`;

export const BarChartCard = styled.div`
  background: var(--color-card-background);
  border-radius: 16px;
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-large);
  transition: ${ANIMATION.transitions.allSlow};
  min-height: 370px;
  min-width: 0;
  display: flex;
  flex-direction: column;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    flex: 3;
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
  }
`;

export const TreemapCard = styled.div`
  background: var(--color-card-background);
  border-radius: 16px;
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-medium);
  transition: ${ANIMATION.transitions.allSlow};
  min-width: 0;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    flex: 1;
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
  }
`;
